import type { CommuneRef, ElementRef } from '@authsemply/shared';
import { api } from './api';

/**
 * Référentiels du portail (14/10/2026) : la saisie de l'identité d'un dossier
 * se fait par recherche dans ces tables, et le serveur refuse une valeur qui
 * n'y figure pas. Les listes courtes (pays, types de voie) sont gardées en
 * mémoire pour la durée de la page.
 */

let pays: Promise<ElementRef[]> | null = null;
let typesVoie: Promise<(ElementRef & { fantoir: boolean })[]> | null = null;

const q = (texte: string) => encodeURIComponent(texte.trim());

/** Un code APE et sa nomenclature (NAF rév. 2, NAF 2025… — 14/10/2026). */
export interface ActiviteRef extends ElementRef {
  nomenclature: string;
  nomenclatureLibelle?: string;
}

/** Un problème renvoyé par le contrôle, rattaché à son champ. */
export interface ProblemeChamp {
  champ: string;
  message: string;
}

export const referentiels = {
  /** `proche` : code postal déjà saisi, dont les communes passent devant. */
  communes: (texte: string, proche = '') =>
    api.get<CommuneRef[]>(`/referentiels/communes?q=${q(texte)}${proche ? `&proche=${q(proche)}` : ''}`),
  activites: (texte: string) => api.get<ActiviteRef[]>(`/referentiels/activites?q=${q(texte)}`),
  /** Codes qui succèdent à un code APE (table de correspondance INSEE). */
  successeurs: (nomenclature: string, code: string) =>
    api.get<ActiviteRef[]>(`/referentiels/successeurs?nomenclature=${q(nomenclature)}&code=${q(code)}`),
  formesJuridiques: (texte: string) => api.get<ElementRef[]>(`/referentiels/formes-juridiques?q=${q(texte)}`),
  pays: () => {
    pays ??= api.get<ElementRef[]>('/referentiels/pays').catch((e: unknown) => {
      pays = null;
      throw e;
    });
    return pays;
  },
  typesVoie: () => {
    typesVoie ??= api.get<(ElementRef & { fantoir: boolean })[]>('/referentiels/types-voie').catch((e: unknown) => {
      typesVoie = null;
      throw e;
    });
    return typesVoie;
  },
  /** Contrôle une identité sans l'enregistrer ; renvoie aussi les valeurs mises au format de référence. */
  verifier: (identite: Record<string, string>) =>
    api.post<{ problemes: ProblemeChamp[]; valeurs: Record<string, string | null> }>('/referentiels/verifier', {
      identite,
    }),
};

/** Les champs en erreur d'une réponse IDENTITE_INCOHERENTE (`details.champs`). */
export function erreursParChamp(details: Record<string, unknown> | undefined): Record<string, string> {
  const champs = details?.champs;
  if (!champs || typeof champs !== 'object') return {};
  return Object.fromEntries(
    Object.entries(champs as Record<string, unknown>).map(([k, v]) => [k, String(v)]),
  );
}
